import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { ChannelLogoPlacement } from "@shared/types/foyer";
import {
  cornerEdges,
  cornerFor,
  type CornerClaimant,
  type PictureSurface,
} from "@shared/tv-corners";

/**
 * A curated effect channel's own watermark, burned into the corner of the
 * video on the TV.
 *
 * **The corner is GRANTED, not chosen here** (`CONTENT-26`): it comes from
 * `@shared/tv-corners`, which ranks every claimant of a corner of the picture
 * and hands out one each. `channel-logo` holds bottom-left on the signage
 * surface, which is what this drew before the authority existed - this
 * component stopped deciding, it did not move. The margin from the granted
 * corner and the rendered width are still the one global admin setting
 * (`GeneralSettingsPage`'s "Channel logo placement" card); `placement.x`/`y`
 * are read as margins from whichever two edges the grant anchors to, so
 * re-ranking the table in one place moves the logo correctly here.
 *
 * Scaled the same way `TemplateSlide.tsx` scales a slide: measure the box
 * this was actually given (not `window` - a CMS preview embed is not
 * full-viewport), compute a single `scale` against the shared 1920x1080
 * design frame, and apply it with one `transform`. The hook is duplicated
 * rather than imported from `TemplateSlide.tsx`, keeping the two renderers
 * decoupled - this is a ~15-line hook, not a shared dependency worth the
 * coupling.
 */

function useBoxSize(ref: React.RefObject<HTMLDivElement | null>): {
  w: number;
  h: number;
} {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setSize({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

export default function ChannelLogoOverlay({
  logoUrl,
  placement,
  surface = "signage",
  claimant = "channel-logo",
  testId,
}: {
  logoUrl: string;
  placement: ChannelLogoPlacement;
  /** Which surface's corner authority applies. Only signage draws one today. */
  surface?: PictureSurface;
  /**
   * WHICH claimant this instance is drawing for, which is what decides its
   * corner. `channel-logo` (bottom-left) is the effect channel's own mark;
   * `supplier-logo` (bottom-right) is the mark of whoever supplied the item
   * playing right now (`CONTENT-26`). One component, because the geometry -
   * the 1920x1080 frame, the single scale transform, margins measured from the
   * granted corner - is identical and only the corner differs.
   */
  claimant?: Extract<CornerClaimant, "channel-logo" | "supplier-logo">;
  testId?: string;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const box = useBoxSize(rootRef);
  // After the hooks, never before: the grant is a module constant for a given
  // surface, but a conditional hook call would still be a lint error waiting
  // for the day somebody makes `surface` dynamic.
  const corner = cornerFor(surface, claimant);
  if (!corner) return null;
  const edges = cornerEdges(corner);
  const anchored: CSSProperties = {
    position: "absolute",
    width: placement.width,
    height: "auto",
    ...(edges.x === "left" ? { left: placement.x } : { right: placement.x }),
    ...(edges.y === "top" ? { top: placement.y } : { bottom: placement.y }),
  };
  // Letterboxed/centered rather than stretched independently on each axis:
  // real deployed panels are physically 16:9, so this is a no-op there, and
  // the only place it could ever diverge is an odd-aspect CMS preview embed,
  // where centered-and-never-distorted is the same trade-off TemplateSlide
  // already makes.
  const scale =
    box.w > 0 && box.h > 0 ? Math.min(box.w / 1920, box.h / 1080) : 0;

  return (
    <div
      ref={rootRef}
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          width: 1920,
          height: 1080,
          margin: "auto",
          transform: `scale(${scale})`,
          transformOrigin: "center center",
        }}
      >
        <img
          src={logoUrl}
          alt=""
          data-testid={testId ?? "channel-logo-overlay"}
          data-corner={corner}
          data-claimant={claimant}
          style={anchored}
        />
      </div>
    </div>
  );
}
